import { store } from "@/store";
import { FeatureKnown } from "@/store/modules/features";
import {
  AllowedEventTypes,
  EventType,
  StandardEventType,
  SystemEventTypes,
  VisibleEventTypes,
} from "../event";
import EphemeralParser from "../parsers/ephemerals";
import EventParser from "../parsers/events";
import { ParsingWay } from "../parsers/types";
import { IJoinedRoom, IRoomEvent, IStateEvent } from "../types";
import ChatRoomEvent, { EventReceiptModes, getEventState } from "./events";
import { ChatRoomMemberCollection } from "./members";
import { ChatRoomReactionsCollection } from "./reactions";
import { ChatRoomNewContentsCollection } from "./newcontents";

export interface ChatRoomCollection {
  [roomId: string]: ChatRoom;
}

export function simplifyRoomId(roomId: string): string {
  return roomId.split(":")[0] || "";
}

/**
 * Computes viewedBy / viewedByAll on each timeline event based on readStatus.
 *
 * Matrix read receipts: a receipt on event X means the user has read
 * everything up to and including X. The timeline is ordered oldest→newest,
 * so we walk it from newest→oldest.  For each user (other than "me"),
 * once we encounter the event they receipted, every event from that point
 * backward is marked as viewed by that user.
 */
function applyReadStatus(
  timeline: IRoomEvent[],
  readStatus: Record<string, string[]>,
  members: ChatRoomMemberCollection,
  iam: string
): void {
  // Build a map: userId → eventId they last read
  const userLastRead: Record<string, string> = {};
  for (const [eventId, userIds] of Object.entries(readStatus)) {
    for (const userId of userIds) {
      if (userId !== iam) {
        userLastRead[userId] = eventId;
      }
    }
  }

  const otherMembers = Object.keys(members).filter((id) => id !== iam);
  if (otherMembers.length === 0) {
    return;
  }

  // For each other user, find at which index their receipt points,
  // then mark all events from index 0..receiptIndex as viewed by them.
  // (timeline is oldest-first at this stage)
  const receiptIndices: Record<string, number> = {};
  for (const userId of otherMembers) {
    const lastReadEventId = userLastRead[userId];
    if (lastReadEventId) {
      const idx = timeline.findIndex((evt) => evt.event_id === lastReadEventId);
      if (idx >= 0) {
        receiptIndices[userId] = idx;
      }
    }
  }

  for (let i = 0; i < timeline.length; i++) {
    const event = timeline[i];
    if (!AllowedEventTypes.includes(event.type as EventType)) {
      continue;
    }
    const viewedBy: string[] = [];
    for (const userId of otherMembers) {
      const receiptIdx = receiptIndices[userId];
      // If the user has a receipt at or after this event, they've read it
      if (receiptIdx !== undefined && i <= receiptIdx) {
        viewedBy.push(userId);
      }
    }
    event.computed = {
      viewedBy,
      viewedByAll: viewedBy.length >= otherMembers.length,
    };
  }
}

export default class ChatRoom {
  readonly id: string;
  readonly fullId: string;
  tribeId: string;
  anonymous: boolean;
  unreadTag: boolean;
  members: ChatRoomMemberCollection;
  timeline: ChatRoomEvent[];
  previousTimelineBatch?: string;
  reactions: ChatRoomReactionsCollection;
  redaction: Array<string>;
  newContents: ChatRoomNewContentsCollection;
  readStatus: Record<string, string[]>;
  unreadNotification: number;
  creationTime?: Date;
  lastEvent?: ChatRoomEvent;
  olderEventTime?: Date;

  constructor(id: string, data: IJoinedRoom, public debug = false) {
    // if (this.debug) console.log("[ChatRoom]", `construct room ${id}`, data);
    this.id = simplifyRoomId(id);
    this.fullId = id;
    this.tribeId = "unknown";
    this.anonymous = false;
    this.unreadTag = false;
    this.members = {};
    this.readStatus = {};
    this.reactions = {};
    this.redaction = [];
    this.creationTime = new Date();
    this.newContents = {};

    if (data.timeline.limited === true) {
      this.previousTimelineBatch = data.timeline.prev_batch;
    }

    const featRead = store.getters["features/enabled"](
      FeatureKnown.oneToOneIsReadBy
    );
    if (
      featRead &&
      data.ephemeral &&
      data.ephemeral.events &&
      data.ephemeral.events.length > 0
    ) {
      const ephemeralParser = new EphemeralParser(this);
      data.ephemeral.events.forEach((evt) => ephemeralParser.parse(evt));
    }

    const eventParser = new EventParser(this, debug);
    data.state.events
      .filter((event) => SystemEventTypes.includes(event.type as EventType))
      .forEach((event) => eventParser.parse(event));

    const timeline = data.timeline.events.filter((event) =>
      AllowedEventTypes.includes(event.type as EventType)
    );
    timeline.forEach((event) => {
      eventParser.parse(event);
    });

    if (featRead) {
      const iam = store.state.chat?.iam || "";
      applyReadStatus(timeline, this.readStatus, this.members, iam);
    }

    this.timeline = timeline
      .filter((event) => VisibleEventTypes.includes(event.type as EventType))
      .reverse()
      .map((event) => {
        if (event.room_id === undefined) {
          event.room_id = this.fullId;
        }
        return new ChatRoomEvent(event, this.id, this.members[event.sender]);
      });

    this.unreadNotification = data.unread_notifications.notification_count || 0;

    if (data.timeline.events.length > 0) {
      const excludeSystemTimeline = data.timeline.events.filter(
        (evt) => !SystemEventTypes.includes(evt.type as EventType)
      );
      if (excludeSystemTimeline.length > 0) {
        const lastEvent =
          excludeSystemTimeline[excludeSystemTimeline.length - 1];
        this.lastEvent = new ChatRoomEvent(
          lastEvent,
          this.fullId,
          this.members[lastEvent.sender]
        );
      }

      this.olderEventTime = this.timeline[this.timeline.length - 1]?.date;
    }

    if (
      data.account_data &&
      data.account_data.events &&
      data.account_data.events.length > 0
    ) {
      data.account_data.events
        .filter((event) => event.type === StandardEventType.Tag)
        .forEach((evt) => {
          this.unreadTag = true;
        });
    }

    // if (debug)
    //   console.log("[ChatRoom] - Constructor end", this, data.timeline.events);
  }

  update(data: Partial<ChatRoom>): void {
    const featRead = store.getters["features/enabled"](
      FeatureKnown.oneToOneIsReadBy
    );
    if (this.debug) console.log("[ChatRoom]", `Updating ${this.id}`, data);
    const featReact = store.getters["features/enabled"](
      FeatureKnown.oneToOneReactions
    );
    if (data.members && Object.keys(data.members).length > 0) {
      Object.keys(data.members).forEach((id) => {
        if (data.members === undefined) {
          return;
        }

        const member = this.members[id]
          ? { ...this.members[id] }
          : data.members[id];
        if (member) {
          member.avatarURL = data.members[id].avatarURL || member.avatarURL;
          member.displayName =
            data.members[id].displayName || member.displayName;
          member.leaved = data.members[id].leaved;
        }
        this.members[id] = member;
      });
    }
    if (
      featRead &&
      data.readStatus &&
      Object.keys(data.readStatus).length > 0
    ) {
      const evtIds = Object.keys(data.readStatus);
      evtIds.forEach((evtId) => {
        if (data.readStatus) {
          data.readStatus[evtId].forEach((usrId) => {
            this.cleanUserReadById(usrId);
            if (this.readStatus[evtId] === undefined) {
              this.readStatus[evtId] = [];
            }
            this.readStatus[evtId].push(usrId);
          });
        }
      });
    }

    if (featReact && data.reactions) {
      if (this.reactions) {
        const existingIds = Object.keys(this.reactions);
        Object.keys(data.reactions).forEach((eventId) => {
          if (data.reactions) {
            if (!existingIds.includes(eventId)) {
              this.reactions[eventId] = data.reactions[eventId];
              return;
            }
            this.reactions[eventId].merge(data.reactions[eventId].iterators);
          }
        });
      } else {
        this.reactions = data.reactions;
      }
    }

    if (data.redaction) {
      if (this.redaction) {
        data.redaction.forEach((redactEventId) => {
          if (this.redaction.indexOf(redactEventId) < 0) {
            this.redaction = this.redaction.concat(redactEventId);
          }
        });
      } else {
        this.redaction = data.redaction;
      }
      // console.log("REDACTS INPUT" + data.redaction);
      // console.log("REDACTS OUTPUT" + this.redaction);
    }

    if (data.newContents) {
      if (this.newContents) {
        const existingIds = Object.keys(this.newContents);
        Object.keys(data.newContents).forEach((eventId) => {
          if (data.newContents) {
            if (!existingIds.includes(eventId)) {
              this.newContents[eventId] = data.newContents[eventId];
              return;
            }
            this.newContents[eventId].merge(
              data.newContents[eventId].iterators
            );
          }
        });
      } else {
        this.newContents = data.newContents;
      }
    }

    if (data.timeline) {
      const incomingTimeline = data.timeline.map((event) => {
        event.sender = this.members[event.event.sender];
        return event;
      });

      this.timeline = [...incomingTimeline, ...this.timeline];
    }

    // Recompute read status on the full timeline after readStatus or timeline changes
    if (featRead) {
      this.recomputeTimelineReadStatus();
    }
    if (data.lastEvent) {
      this.lastEvent = data.lastEvent;
    }
    this.unreadNotification =
      data.unreadNotification || this.unreadNotification;
    if (this.debug) console.log("[ChatRoom] - Updated ", this, data);
  }

  prependEvents(
    events: Array<IRoomEvent | IStateEvent>,
    mode = EventReceiptModes.Matrix
  ): void {
    if (this.debug) console.log("[ChatRoom] - PrependEvents ", events, mode);
    const timeline = events.filter((event) =>
      AllowedEventTypes.includes(event.type as EventType)
    );

    if (events.length === 0 || timeline.length === 0) {
      return;
    }
    const eventParser = new EventParser(this);
    timeline.forEach((event) => eventParser.parse(event, ParsingWay.Backward));

    this.timeline.push(
      ...timeline
        .filter((event) => VisibleEventTypes.includes(event.type as EventType))
        .map((event) => {
          if (event.room_id === undefined) {
            event.room_id = this.fullId;
          }
          return new ChatRoomEvent(
            event,
            this.id,
            this.members[event.sender],
            mode
          );
        })
    );

    this.olderEventTime = new Date(this.timeline[this.timeline.length - 1].ts);

    if (this.lastEvent === undefined) {
      const excludeSystemTimeline = events.filter(
        (evt) => !SystemEventTypes.includes(evt.type as EventType)
      );
      let event = excludeSystemTimeline[0];
      if (event.event_id === undefined) {
        event = event.properties as IRoomEvent;
      }
      this.lastEvent = new ChatRoomEvent(
        event,
        this.fullId,
        this.members[event.sender],
        mode
      );
    }
    if (this.debug) console.log("[ChatRoom] - Prepended ", this, events, mode);
  }

  get isClosed(): boolean {
    if (this.members === undefined) {
      return true;
    }

    return (
      Object.values(this.members).filter((member) => !member.leaved).length < 2
    );
  }

  get lastEventDate(): Date | undefined {
    return this.lastEvent ? this.lastEvent.date : this.creationTime;
  }

  get cleanTimeline(): ChatRoomEvent[] {
    return this.timeline.filter((evt) => {
      const AllowedTypes = AllowedEventTypes;
      return AllowedTypes.includes(evt.type);
    });
  }

  private recomputeTimelineReadStatus(): void {
    const iam = store.state.chat?.iam || "";
    // timeline is newest-first in the ChatRoom, applyReadStatus expects oldest-first
    const rawEvents = [...this.timeline].reverse().map((evt) => evt.event);
    applyReadStatus(
      rawEvents as IRoomEvent[],
      this.readStatus,
      this.members,
      iam
    );
    // Update each ChatRoomEvent's state from the recomputed event.computed
    this.timeline.forEach((evt) => {
      evt.state = getEventState(evt.event);
    });
  }

  private cleanUserReadById(id: string): void {
    const evtIds = Object.keys(this.readStatus);
    evtIds.forEach((evtId) => {
      const pos = this.readStatus[evtId].indexOf(id);
      if (pos >= 0) {
        this.readStatus[evtId].splice(pos, 1);
      }
      if (this.readStatus[evtId].length === 0) {
        delete this.readStatus[evtId];
      }
    });
  }
}
